import React, { useCallback, useMemo, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  CoinsFill as SpecificCurrencyIcon,
  PencilFill as EditIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import Radio from "@hl/base-components/lib/assets/icons.generated/Radio";
import RadioFilled from "@hl/base-components/lib/assets/icons.generated/RadioFilled";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import {
  STATUS_COLOR,
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import {
  networkByNameLookup,
  NetworkTypeData,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { ethers } from "ethers";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";

import {
  CollectionStandardType,
  Condition,
  ConditionType,
  CurrencyStandardType,
  NetworkType,
} from "apollo/graphql.generated";
import { ReactComponent as Erc20Icon } from "assets/icons/erc-20.svg";
import BlockchainIcon from "components/BlockchainIcon";
import {
  CurrencyDetailsQuery,
  useCurrencyDetailsQuery,
} from "~/features/gates/queries/gates.graphql.generated";

export type FindCurrencyProps = {
  onSave: (data: Condition) => void;
  submitText?: string;
  initialCondition?: Condition;
};

const supportedNetworks = [
  NetworkType.ETHEREUM,
  NetworkType.POLYGON,
  NetworkType.BASE,
  NetworkType.OPTIMISM,
  NetworkType.ARBITRUM,
  NetworkType.ZORA,
];

const SetCurrency = ({
  onSave,
  submitText = "Add condition",
  initialCondition,
}: FindCurrencyProps) => {
  const initialAmount = initialCondition?.amount ?? undefined;
  const initialActiveTab = initialCondition?.data?.standard === "ERC20" ? 1 : 0;
  const initialErc20Address =
    initialActiveTab === 1
      ? initialCondition?.data?.address ?? undefined
      : undefined;
  const initialNetwork =
    (initialCondition?.data?.network as NetworkType) ??
    (initialActiveTab === 0 ? supportedNetworks.at(0) : undefined);

  const [network, setNetwork] = useState<NetworkType | undefined>(
    initialNetwork
  );
  const [amount, setAmount] = useState<number | undefined>(initialAmount);
  const [activeTab, setActiveTab] = useState<number>(initialActiveTab);
  const [erc20Address, setErc20Address] = useState<string | undefined>(
    initialErc20Address
  );
  const { data: erc20CurrencyDetails, loading } = useCurrencyDetailsQuery({
    variables: { address: erc20Address!, network: network! },
    skip: !(erc20Address && network),
  });

  const changeTab = (tab: number) => {
    setAmount(undefined);
    setErc20Address(undefined);
    setActiveTab(tab);
    setNetwork(tab === 1 ? undefined : supportedNetworks.at(0));
  };

  const addCondition = useCallback(() => {
    const networkDetails = networkByNameLookup(network);
    if (
      activeTab === 1 &&
      amount !== undefined &&
      erc20Address &&
      erc20CurrencyDetails
    ) {
      onSave({
        type: ConditionType.CURRENCY,
        amount,
        data: {
          address: erc20Address,
          network: networkDetails.type,
          imageUrl: erc20CurrencyDetails.currencyDetails.imageUrl ?? "",
          name: erc20CurrencyDetails.currencyDetails.symbol ?? "",
          standard: CurrencyStandardType.ERC20,
        },
      });
    }
    if (amount !== undefined && activeTab === 0) {
      onSave({
        type: ConditionType.CURRENCY,
        amount,
        data: {
          address: ethers.constants.AddressZero,
          network: networkDetails.type,
          name: networkDetails.currencyName,
          standard: CurrencyStandardType.NATIVE,
        },
      });
    }
  }, [network, activeTab, amount, erc20Address, erc20CurrencyDetails, onSave]);
  return (
    <>
      <Group mb="sm" spacing={8}>
        <Button
          sx={{
            border: "none",
          }}
          size="sm"
          color={activeTab === 0 ? SECONDARY_COLOR : OUTLINE_COLOR}
          onClick={() => changeTab(0)}
          px="sm"
          leftIcon={<SpecificCurrencyIcon height={16} />}
        >
          Native tokens
        </Button>
        <Button
          sx={{
            border: "none",
          }}
          size="sm"
          color={activeTab === 1 ? SECONDARY_COLOR : OUTLINE_COLOR}
          onClick={() => changeTab(1)}
          px="sm"
          leftIcon={<Erc20Icon height={16} />}
        >
          ERC-20s
        </Button>
      </Group>
      <Divider mx={"-md"} />

      <Stack pt={"md"} spacing={"xl"}>
        {activeTab === 0 && (
          <Box>
            <Text pb={"xs"} size="sm" weight={500}>
              What blockchain is this token on?
            </Text>
            <BlockchainRadioSelection
              availableNetworks={supportedNetworks}
              network={network}
              onNetworkSelection={setNetwork}
              loading={loading}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <>
            <ERC20CurrencyInput
              details={erc20CurrencyDetails}
              loading={loading}
              setNetwork={setNetwork}
              onEnterContract={setErc20Address}
            />
            {erc20Address != null && (network == null || loading) && (
              <Box>
                <Text pb="sm" size="sm" weight={500}>
                  What blockchain is this token on?
                </Text>
                <BlockchainRadioSelection
                  availableNetworks={supportedNetworks}
                  network={network}
                  onNetworkSelection={setNetwork}
                  loading={loading}
                />
              </Box>
            )}
            <ERC20CurrencyDetailsSection
              details={erc20CurrencyDetails}
              network={network}
              onClear={() => {
                changeTab(activeTab);
              }}
            />
          </>
        )}

        {network && (
          <CurrencyAmountInput
            details={erc20CurrencyDetails}
            network={network}
            amount={amount}
            onSetAmount={setAmount}
          />
        )}
      </Stack>
      <ModalSection>
        <Button
          size="xl"
          fullWidth
          onClick={addCondition}
          disabled={(amount ?? 0) === 0}
        >
          {submitText}
        </Button>
      </ModalSection>
    </>
  );
};

type ERC20CurrencyInputProps = {
  details?: CurrencyDetailsQuery;
  onEnterContract: React.Dispatch<React.SetStateAction<string | undefined>>;
  loading: boolean;
  setNetwork: React.Dispatch<React.SetStateAction<NetworkType | undefined>>;
};

const ERC20CurrencyInput = ({
  details,
  onEnterContract,
  loading,
  setNetwork,
}: ERC20CurrencyInputProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (ethers.utils.isAddress(val)) {
        onEnterContract(val);
      } else {
        onEnterContract(undefined);
      }

      // handle error case, reset network selection
      if (
        !details ||
        (details.currencyDetails && !details.currencyDetails.standard)
      ) {
        setNetwork(undefined);
      }
    },
    [details, onEnterContract, setNetwork]
  );

  if (details && details.currencyDetails && details.currencyDetails.standard)
    return <></>;

  return (
    <Box>
      <TextInput
        placeholder="Contract address"
        label="Enter the token’s contract address"
        onChange={handleChange}
        rightSection={loading && <Loader size="sm" />}
      />
      {details && (
        <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
          Error finding ERC20 Currency
        </Text>
      )}
    </Box>
  );
};

type CurrencyAmountInputProps = {
  details?: CurrencyDetailsQuery;
  amount?: number;
  network: NetworkType;
  onSetAmount: (amount: number) => void;
};

const CurrencyAmountInput = ({
  details,
  network,
  amount,
  onSetAmount,
}: CurrencyAmountInputProps) => {
  const networkDetails = networkByNameLookup(network);
  const currencySymbol =
    details?.currencyDetails?.symbol ?? networkDetails.currencySymbol;
  const chainId = networkDetails.chainId;

  return (
    <>
      <CryptoCurrencyInput
        chainId={chainId}
        missingChainIdPlaceholder={"Unknown chain"}
        label={"Minimum balance required"}
        description={"Specify the minimum balance for access"}
        customCurrencySymbol={currencySymbol}
        value={amount}
        onChange={(e) => onSetAmount(e ?? 0)}
        precision={10}
        sx={{ flexGrow: 1 }}
        placeholder="0.1"
      />
    </>
  );
};

type ERC20CurrencyDetailsSectionProps = {
  details?: CurrencyDetailsQuery;
  network?: NetworkType;
  onClear: () => void;
};

const ERC20CurrencyDetailsSection = ({
  details,
  network,
  onClear,
}: ERC20CurrencyDetailsSectionProps) => {
  if (!details) {
    return <></>;
  }

  let imageUrl = "";
  let name = "";
  let standard = "";

  if (details && details.currencyDetails) {
    imageUrl = details.currencyDetails.imageUrl || "";
    name = details.currencyDetails.name || "Token";
    standard = details.currencyDetails.standard?.toString() || "";
  }

  if (!standard) {
    return <></>;
  }

  return (
    <BoxContainer py={12} px={16}>
      <Grid align="center" justify="space-between">
        <Grid.Col span="content">
          <Center
            w={68}
            h={68}
            bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}
            sx={(theme) => ({
              border: `1px solid ${theme.colors.divider[0]}`,
              overflow: "hidden",
              borderRadius: "100%",
            })}
          >
            {imageUrl ? (
              <Image
                src={imageUrl}
                fit="contain"
                width={68}
                height={68}
                withPlaceholder
              />
            ) : (
              <BlockchainIcon network={network} />
            )}
          </Center>
        </Grid.Col>
        <Grid.Col span="auto">
          <Text fw={500} size={"sm"}>
            {name}
          </Text>
          <Text size="xs" color={TEXT_COLOR.SECONDARY}>
            {standard == CollectionStandardType.OPENSEA ? "OpenSea" : standard}
            {"∙"}
            {maskAddress(details.currencyDetails.contract, 6, 4)}
          </Text>
        </Grid.Col>
        <Grid.Col span="content">
          <Button
            color={SECONDARY_COLOR}
            onClick={onClear}
            leftIcon={<EditIcon />}
          >
            Edit
          </Button>
        </Grid.Col>
      </Grid>
    </BoxContainer>
  );
};

export default SetCurrency;

const BlockchainRadioSelection = ({
  availableNetworks,
  network,
  onNetworkSelection,
  loading,
}: {
  availableNetworks: NetworkType[];
  network?: NetworkType;
  onNetworkSelection: (network: NetworkType) => void;
  loading?: boolean;
}) => {
  const { isMedium } = useMantineMediaQueries();
  const networksDetails = useMemo(() => {
    return availableNetworks
      .map(networkByNameLookup)
      .filter((c) => c != null) as NetworkTypeData[];
  }, [availableNetworks]);

  if (isMedium) {
    return (
      <BoxContainer py={0}>
        <Flex gap="md" columnGap="50">
          <Stack sx={{ flexGrow: 1, flexBasis: 0 }} spacing={0}>
            {networksDetails.map((n, i) => {
              if (i % 2 == 0)
                return (
                  <>
                    <BlockchainRadioTile
                      key={i}
                      network={n.type}
                      isChecked={network === n.type}
                      isLoading={loading && network === n.type}
                      onClick={() => onNetworkSelection(n.type)}
                    />
                    {i < networksDetails.length - 2 && <Divider />}
                  </>
                );
              return null;
            })}
          </Stack>

          <Box py={"sm"}>
            <Box
              sx={(theme) => ({
                width: 1,
                height: "100%",
                background: theme.colors.divider[0],
              })}
            ></Box>
          </Box>

          <Stack sx={{ flexGrow: 1, flexBasis: 0 }} spacing={0}>
            {networksDetails.map((n, i) => {
              if (i % 2 == 1)
                return (
                  <>
                    <BlockchainRadioTile
                      key={i}
                      network={n.type}
                      isChecked={network === n.type}
                      isLoading={loading && network === n.type}
                      onClick={() => onNetworkSelection(n.type)}
                    />
                    {i < networksDetails.length - 1 && <Divider />}
                  </>
                );
              return null;
            })}
          </Stack>
        </Flex>
      </BoxContainer>
    );
  }

  return (
    <Stack spacing={0}>
      {networksDetails.map((n, i) => (
        <BlockchainRadioTile
          key={i}
          network={n.type}
          isChecked={network === n.type}
          isLoading={loading && network === n.type}
          onClick={() => onNetworkSelection(n.type)}
        />
      ))}
    </Stack>
  );
};

const BlockchainRadioTile = ({
  network,
  isChecked,
  isLoading,
  onClick,
}: {
  network: NetworkType;
  isChecked?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}) => {
  const networkDetails = useMemo(() => networkByNameLookup(network), [network]);
  const icon = useMemo(() => {
    return <BlockchainIcon network={networkDetails.type} />;
  }, [networkDetails]);
  const title = useMemo(() => {
    return networkDetails.displayName || networkDetails.currencyName;
  }, [networkDetails]);

  return (
    <Flex
      gap={10}
      h={46}
      align={"center"}
      onClick={onClick}
      sx={{ cursor: "pointer" }}
    >
      {isLoading ? <Loader w={24} h={24} /> : icon}
      <Text sx={{ flexGrow: 1 }}>{title}</Text>
      {isChecked ? (
        <Center>
          <RadioFilled />
        </Center>
      ) : (
        <Center
          sx={(theme) => ({
            color: theme.colors.divider[0],
          })}
        >
          <Radio />
        </Center>
      )}
    </Flex>
  );
};
